import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../../stores/authStore';
import { scoringApi } from '../../services/scoring';
import { vendorApi } from '../../services/vendor';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';
import { toGGScore, toGGDelta } from '../../utils/score';
import type { ScoringHistoryItem } from '../../types';

type TabKey = 'vendor' | 'sites' | 'guards';

const severityColors: Record<string, string> = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-slate-100 text-slate-600',
};

const categoryColors: Record<string, string> = {
  patrol: 'bg-blue-100 text-blue-700',
  incident: 'bg-red-100 text-red-700',
  compliance: 'bg-purple-100 text-purple-700',
  reporting: 'bg-cyan-100 text-cyan-700',
  attendance: 'bg-emerald-100 text-emerald-700',
  quality: 'bg-indigo-100 text-indigo-700',
};

function formatWeek(weekStart: string | null): string {
  if (!weekStart) return '--';
  const d = new Date(weekStart + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getCategoryBadgeClass(category: string): string {
  const key = category.toLowerCase();
  return categoryColors[key] || 'bg-slate-100 text-slate-600';
}

export default function VendorScoringHistory() {
  const user = useAuthStore((s) => s.user);
  const [activeTab, setActiveTab] = useState<TabKey>('vendor');
  const [selectedEntityId, setSelectedEntityId] = useState<string>('');
  const [page, setPage] = useState(1);

  const orgId = user?.organization || '';

  // Fetch sites and guards for dropdown selectors
  const { data: sitesData } = useQuery({
    queryKey: ['vendor', 'sites', 'all'],
    queryFn: () => vendorApi.getSites({ page_size: '100' }).then((r) => r.data),
    enabled: activeTab === 'sites',
  });

  const { data: guardsData } = useQuery({
    queryKey: ['vendor', 'guards', 'all'],
    queryFn: () => vendorApi.getGuards({ page_size: '100' }).then((r) => r.data),
    enabled: activeTab === 'guards',
  });

  // Build query params for scoring history
  const queryParams: { entity_type?: string; entity_id?: string; page: number } = { page };
  if (activeTab === 'vendor') {
    queryParams.entity_type = 'vendor';
    queryParams.entity_id = orgId;
  } else if (activeTab === 'sites' && selectedEntityId) {
    queryParams.entity_type = 'site';
    queryParams.entity_id = selectedEntityId;
  } else if (activeTab === 'guards' && selectedEntityId) {
    queryParams.entity_type = 'guard';
    queryParams.entity_id = selectedEntityId;
  }

  const canFetch =
    activeTab === 'vendor'
      ? !!orgId
      : !!selectedEntityId;

  const { data, isLoading } = useQuery({
    queryKey: ['scoring-history', activeTab, queryParams.entity_id, page],
    queryFn: () => scoringApi.getVendorScoringHistory(queryParams).then((r) => r.data),
    enabled: canFetch,
  });

  const items: ScoringHistoryItem[] = data?.results ?? [];
  const totalCount = data?.count ?? 0;
  const pageSize = 20;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const handleTabChange = (tab: TabKey) => {
    setActiveTab(tab);
    setSelectedEntityId('');
    setPage(1);
  };

  const handleEntityChange = (entityId: string) => {
    setSelectedEntityId(entityId);
    setPage(1);
  };

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'vendor', label: 'Vendor' },
    { key: 'sites', label: 'Sites' },
    { key: 'guards', label: 'Guards' },
  ];

  return (
    <>
      <PageHeader
        title="Scoring History"
        description="Complete history of all rule triggers and score changes"
      />

      {/* Tabs */}
      <div className="mb-6 flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => handleTabChange(tab.key)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === tab.key
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Entity selector for Sites / Guards tabs */}
      {activeTab !== 'vendor' && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            Select {activeTab === 'sites' ? 'Site' : 'Guard'}
          </label>
          <select
            value={selectedEntityId}
            onChange={(e) => handleEntityChange(e.target.value)}
            className="w-full max-w-sm rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
          >
            <option value="">
              -- Select a {activeTab === 'sites' ? 'site' : 'guard'} --
            </option>
            {activeTab === 'sites' &&
              (sitesData?.results ?? []).map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            {activeTab === 'guards' &&
              (guardsData?.results ?? []).map((guard) => (
                <option key={guard.id} value={guard.id}>
                  {guard.name}
                </option>
              ))}
          </select>
        </div>
      )}

      {/* Prompt to select entity */}
      {activeTab !== 'vendor' && !selectedEntityId && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 py-8">
          <EmptyState
            title={`Select a ${activeTab === 'sites' ? 'site' : 'guard'}`}
            description={`Choose a ${activeTab === 'sites' ? 'site' : 'guard'} above to view its scoring history.`}
            icon={
              <svg className="w-6 h-6 text-slate-400" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 010 3.75H5.625a1.875 1.875 0 010-3.75z" />
              </svg>
            }
          />
        </div>
      )}

      {/* Loading state */}
      {canFetch && isLoading && (
        <div className="flex justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {/* Empty state */}
      {canFetch && !isLoading && items.length === 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 py-8">
          <EmptyState
            title="No scoring history"
            description="No rule triggers have been recorded yet for this entity."
            icon={
              <svg className="w-6 h-6 text-slate-400" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
        </div>
      )}

      {/* Results table */}
      {canFetch && !isLoading && items.length > 0 && (
        <>
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/50">
                    <th className="text-left px-5 py-3 font-semibold text-slate-600">Week</th>
                    <th className="text-left px-5 py-3 font-semibold text-slate-600">Rule</th>
                    <th className="text-left px-5 py-3 font-semibold text-slate-600">Entity</th>
                    <th className="text-left px-5 py-3 font-semibold text-slate-600">Category</th>
                    <th className="text-left px-5 py-3 font-semibold text-slate-600">Severity</th>
                    <th className="text-right px-5 py-3 font-semibold text-slate-600">Impact</th>
                    <th className="text-right px-5 py-3 font-semibold text-slate-600">Score Before</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map((item) => {
                    const ggDelta = toGGDelta(item.score_impact);
                    const ggScoreBefore = toGGScore(item.score_before);
                    const isPositive = item.is_positive;
                    return (
                      <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-5 py-3 whitespace-nowrap text-slate-700">
                          {formatWeek(item.week_start)}
                        </td>
                        <td className="px-5 py-3">
                          <div className="font-medium text-slate-900">{item.rule_name}</div>
                          <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                            {item.rule_description || item.explanation}
                          </div>
                        </td>
                        <td className="px-5 py-3 whitespace-nowrap text-slate-700">
                          {item.entity_name}
                        </td>
                        <td className="px-5 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getCategoryBadgeClass(item.rule_category)}`}>
                            {item.rule_category}
                          </span>
                        </td>
                        <td className="px-5 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${severityColors[item.severity_level] || severityColors.low}`}>
                            {item.severity_level}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-right whitespace-nowrap">
                          <span className={`font-mono font-semibold ${isPositive ? 'text-emerald-600' : 'text-red-600'}`}>
                            {ggDelta > 0 ? '+' : ''}{ggDelta}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-right whitespace-nowrap font-medium text-slate-700">
                          {ggScoreBefore}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-center gap-4">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                </svg>
                Previous
              </button>
              <span className="text-sm text-slate-600">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                </svg>
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
}
